import { useState } from 'react';
import HotelGeoMap from '../components/investigation/HotelGeoMap';
import {
  searchNearbyHotels,
  HotelGeoData,
} from '../services/investigationApi';

function InvestigationGeoPage() {
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [radiusKm, setRadiusKm] = useState('5');
  const [hotels, setHotels] = useState<HotelGeoData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState<HotelGeoData | null>(null);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);
    const radius = parseFloat(radiusKm);

    if (isNaN(lat) || isNaN(lng) || isNaN(radius) || radius <= 0) {
      setError('Veuillez entrer des coordonnées et un rayon valides');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await searchNearbyHotels({
        latitude: lat,
        longitude: lng,
        radius_km: radius,
        limit: 50,
      });
      setHotels(result.hotels || []);
      setSearched(true);
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la recherche');
      setHotels([]);
    } finally {
      setLoading(false);
    }
  };

  const center = latitude && longitude
    ? { lat: parseFloat(latitude), lng: parseFloat(longitude) }
    : undefined;

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">🗺️ Géolocalisation Hôtels</h1>
        <p className="text-gray-600">Recherche d'hôtels par proximité géographique</p>
      </div>

      {/* Search Form */}
      <form onSubmit={handleSearch} className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label htmlFor="geo-lat" className="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
            <input id="geo-lat" type="number" step="any" value={latitude} onChange={(e) => setLatitude(e.target.value)}
              placeholder="-4.3217" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
          <div>
            <label htmlFor="geo-lng" className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
            <input id="geo-lng" type="number" step="any" value={longitude} onChange={(e) => setLongitude(e.target.value)}
              placeholder="15.3125" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
          <div>
            <label htmlFor="geo-radius" className="block text-sm font-medium text-gray-700 mb-1">Rayon (km)</label>
            <input id="geo-radius" type="number" step="0.5" min="0.1" value={radiusKm} onChange={(e) => setRadiusKm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
          <div className="flex items-end">
            <button type="submit" disabled={loading}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium">
              {loading ? 'Recherche...' : 'Rechercher'}
            </button>
          </div>
        </div>
      </form>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-700">{error}</div>
      )}

      {/* Map + Hotel List */}
      {searched && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Map */}
          <div className="lg:col-span-2 h-[500px]">
            <HotelGeoMap
              hotels={hotels}
              center={center}
              zoom={12}
              onHotelClick={setSelectedHotel}
            />
          </div>

          {/* Hotel List Panel */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">{hotels.length} hôtel(s) trouvé(s)</h3>
            </div>
            <div className="overflow-y-auto max-h-[450px] divide-y divide-gray-100">
              {hotels.length === 0 ? (
                <p className="p-4 text-gray-400 text-sm text-center">Aucun hôtel dans ce rayon</p>
              ) : hotels.map((hotel) => (
                <div
                  key={hotel.hotel_id}
                  className={`p-4 cursor-pointer hover:bg-blue-50 transition ${
                    selectedHotel?.hotel_id === hotel.hotel_id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                  }`}
                  onClick={() => setSelectedHotel(hotel)}
                >
                  <p className="font-medium text-sm text-gray-900">{hotel.name}</p>
                  <p className="text-xs text-gray-500 mt-1">{hotel.full_address}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{hotel.province} / {hotel.commune}</p>
                  {hotel.latitude != null && hotel.longitude != null && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      GPS: {hotel.latitude.toFixed(4)}, {hotel.longitude.toFixed(4)}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InvestigationGeoPage;
